import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";

import { i18n } from "../../translate/i18n";
import { head } from "lodash";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";
import Autocomplete from "@material-ui/lab/Autocomplete";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
}));

const AnnouncementSchema = Yup.object().shape({
  title: Yup.string().required("Obrigatório"),
  text: Yup.string().required("Obrigatório"),
});

const AnnouncementModal = ({
  open,
  onClose,
  announcementId,
  reload,
  resetPagination
}) => {
  const classes = useStyles();

  const initialState = {
    title: "",
    text: "",
    priority: 3,
    status: true,
    targetCompanyId: "",
    targetCompanyIds: [],
    type: "general",
    expiresAt: "",
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [announcement, setAnnouncement] = useState(initialState);
  const [companies, setCompanies] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);

  // VENTURA 6.1A - destino, categoria e expiracao
  const toDateTimeLocal = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    const pad = (number) => String(number).padStart(2, "0");

    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}`;
  };

  useEffect(() => {
    if (!open) return undefined;

    let mounted = true;

    const loadCompanies = async () => {
      try {
        const { data } = await api.get("/companies/list");

        if (mounted) {
          setCompanies(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        toastError(err);
      }
    };

    loadCompanies();

    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    try {
      (async () => {
        if (!announcementId) return;

        const { data } = await api.get(`/announcements/${announcementId}`);
        setAnnouncement((prevState) => {
          return {
            ...prevState,
            ...data,
            targetCompanyId: data.targetCompanyId ?? "",
            targetCompanyIds:
              Array.isArray(data.targetCompanies) &&
              data.targetCompanies.length > 0
                ? data.targetCompanies.map(
                    (company) => Number(company.id)
                  )
                : data.targetCompanyId
                  ? [Number(data.targetCompanyId)]
                  : [],
            type: data.type || "general",
            expiresAt: toDateTimeLocal(data.expiresAt),
          };
        });
      })();
    } catch (err) {
      toastError(err);
    }
  }, [announcementId, open]);

  const handleClose = () => {
    setAnnouncement(initialState);
    setAttachment(null);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveAnnouncement = async (values) => {
    const normalizedTargetCompanyIds =
      Array.from(
        new Set(
          (values.targetCompanyIds || [])
            .map((id) => Number(id))
            .filter(
              (id) =>
                Number.isInteger(id) &&
                id > 0
            )
        )
      );

    const announcementData = {
      ...values,
      targetCompanyIds:
        normalizedTargetCompanyIds,
      targetCompanyId:
        normalizedTargetCompanyIds.length > 0
          ? normalizedTargetCompanyIds[0]
          : null,
      expiresAt: values.expiresAt
        ? new Date(values.expiresAt).toISOString()
        : null,
    };

    try {
      if (announcementId) {
        await api.put(`/announcements/${announcementId}`, announcementData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "announcements");
          formData.append("file", attachment);
          await api.post(
            `/announcements/${announcementId}/media-upload`,
            formData
          );
        }
      } else {
        const { data } = await api.post("/announcements", announcementData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "announcements");
          formData.append("file", attachment);

          await api.post(`/announcements/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("announcements.toasts.success"));
      if (typeof reload == "function") {
        reload();
      } else if (typeof resetPagination == "function") {
        resetPagination();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (announcement.mediaPath) {
      await api.delete(`/announcements/${announcement.id}/media-upload`);
      setAnnouncement((prev) => ({
        ...prev,
        mediaPath: null,
      }));
      toast.success(i18n.t("announcements.toasts.deleted"));
      if (typeof reload == "function") {
        reload();
      } else if (typeof resetPagination == "function") {
        resetPagination();
      }
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("announcements.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {announcementId
            ? `${i18n.t("announcements.dialog.edit")}`
            : `${i18n.t("announcements.dialog.add")}`}
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            accept=".png,.jpg,.jpeg"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={announcement}
          enableReinitialize={true}
          validationSchema={AnnouncementSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveAnnouncement(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({
              touched,
              errors,
              isSubmitting,
              values,
              setFieldValue
            }) => (
            <Form>
              <DialogContent dividers>
                <Grid spacing={2} container>
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      label={i18n.t("announcements.dialog.form.title")}
                      name="title"
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      label={i18n.t("announcements.dialog.form.text")}
                      name="text"
                      error={touched.text && Boolean(errors.text)}
                      helperText={touched.text && errors.text}
                      variant="outlined"
                      margin="dense"
                      multiline={true}
                      rows={7}
                      fullWidth
                    />
                  </Grid>
                  {/* VENTURA 6.1A - DESTINO MULTIEMPRESA */}
                  <Grid xs={12} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    >
                      <Autocomplete
                        multiple
                        fullWidth
                        size="small"
                        options={companies}
                        value={companies.filter(
                          (company) =>
                            (
                              values.targetCompanyIds ||
                              []
                            ).includes(
                              Number(company.id)
                            )
                        )}
                        getOptionLabel={(option) =>
                          option?.name || ""
                        }
                        getOptionSelected={(
                          option,
                          value
                        ) =>
                          Number(option.id) ===
                          Number(value.id)
                        }
                        onChange={(
                          event,
                          selectedCompanies
                        ) => {
                          const ids =
                            selectedCompanies.map(
                              (company) =>
                                Number(company.id)
                            );

                          setFieldValue(
                            "targetCompanyIds",
                            ids
                          );

                          setFieldValue(
                            "targetCompanyId",
                            ids.length > 0
                              ? ids[0]
                              : ""
                          );
                        }}
                        noOptionsText="Nenhuma empresa encontrada"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="Destino"
                            placeholder={
                              (
                                values.targetCompanyIds ||
                                []
                              ).length === 0
                                ? "Todas as empresas"
                                : ""
                            }
                            helperText={
                              (
                                values.targetCompanyIds ||
                                []
                              ).length === 0
                                ? "Sem seleção: todas as empresas"
                                : `${values.targetCompanyIds.length} empresa(s) selecionada(s)`
                            }
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  {/* VENTURA 6.1A - CATEGORIA */}
                  <Grid xs={12} item>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="type-selection-label">
                        Categoria
                      </InputLabel>

                      <Field
                        as={Select}
                        label="Categoria"
                        labelId="type-selection-label"
                        id="type"
                        name="type"
                      >
                        <MenuItem value="general">
                          Geral
                        </MenuItem>

                        <MenuItem value="news">
                          Novidade
                        </MenuItem>

                        <MenuItem value="maintenance">
                          Manutenção
                        </MenuItem>

                        <MenuItem value="financial">
                          Financeiro
                        </MenuItem>

                        <MenuItem value="system">
                          Sistema
                        </MenuItem>

                        {values.type === "birthday" && (
                          <MenuItem value="birthday" disabled>
                            Aniversário automático
                          </MenuItem>
                        )}
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* VENTURA 6.1A - EXPIRACAO */}
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      label="Expira em"
                      name="expiresAt"
                      type="datetime-local"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      helperText="Opcional. Deixe vazio para não expirar."
                    />
                  </Grid>

                  <Grid xs={12} item>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="status-selection-label">
                        {i18n.t("announcements.dialog.form.status")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("announcements.dialog.form.status")}
                        placeholder={i18n.t("announcements.dialog.form.status")}
                        labelId="status-selection-label"
                        id="status"
                        name="status"
                        error={touched.status && Boolean(errors.status)}
                      >
                        <MenuItem value={true}>{i18n.t("announcements.dialog.form.active")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("announcements.dialog.form.inactive")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} item>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="priority-selection-label">
                        {i18n.t("announcements.dialog.form.priority")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("announcements.dialog.form.priority")}
                        placeholder={i18n.t(
                          "announcements.dialog.form.priority"
                        )}
                        labelId="priority-selection-label"
                        id="priority"
                        name="priority"
                        error={touched.priority && Boolean(errors.priority)}
                      >
                        <MenuItem value={1}>{i18n.t("announcements.dialog.form.high")}</MenuItem>
                        <MenuItem value={2}>{i18n.t("announcements.dialog.form.medium")}</MenuItem>
                        <MenuItem value={3}>{i18n.t("announcements.dialog.form.low")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  {(announcement.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <Button startIcon={<AttachFileIcon />}>
                        {attachment ? attachment.name : announcement.mediaName}
                      </Button>
                      <IconButton
                        onClick={() => setConfirmationOpen(true)}
                        color="secondary"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                {!attachment && !announcement.mediaPath && (
                  <Button
                    color="primary"
                    onClick={() => attachmentFile.current.click()}
                    disabled={isSubmitting}
                    variant="outlined"
                  >
                    {i18n.t("announcements.dialog.buttons.attach")}
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("announcements.dialog.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {announcementId
                    ? `${i18n.t("announcements.dialog.buttons.edit")}`
                    : `${i18n.t("announcements.dialog.buttons.add")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default AnnouncementModal;
